import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, CogIcon, Cog6ToothIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { accountMappingService } from '../../../services/accounting/account-mapping';
import { chartOfAccountsService } from '../../../services/accounting/chart-of-accounts';
import toast from 'react-hot-toast';

/**
 * AccountingSettingsModal Component
 * 
 * Modal for configuring account mappings and automation settings
 */
const AccountingSettingsModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mappings, setMappings] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState('mappings');
  const [automationSettings, setAutomationSettings] = useState({});

  // Expense categories with their display names
  const expenseCategories = [
    { id: 'fuel', name: t('expenses.fuel_expenses', 'Fuel'), icon: '⛽' },
    { id: 'maintenance', name: t('expenses.maintenance_expenses', 'Maintenance'), icon: '🔧' },
    { id: 'salary', name: t('expenses.salary_expenses', 'Salary'), icon: '👥' },
    { id: 'commission', name: t('expenses.commission_expenses', 'Commission'), icon: '💼' },
    { id: 'office', name: t('expenses.office_expenses', 'Office Supplies'), icon: '📋' },
    { id: 'marketing', name: t('expenses.marketing_expenses', 'Marketing'), icon: '📢' },
    { id: 'insurance', name: t('expenses.insurance_expenses', 'Insurance'), icon: '🛡️' },
    { id: 'utilities', name: t('expenses.utilities_expenses', 'Utilities'), icon: '💡' },
    { id: 'rent', name: t('expenses.rent_expenses', 'Rent'), icon: '🏢' },
    { id: 'professional_services', name: t('expenses.professional_services_expenses', 'Professional Services'), icon: '⚖️' },
    { id: 'equipment', name: t('expenses.equipment_expenses', 'Equipment'), icon: '🛠️' },
    { id: 'oil_change', name: t('expenses.oil_change_expenses', 'Oil Changes'), icon: '🛢️' },
    { id: 'other', name: t('expenses.other_expenses', 'Other'), icon: '📋' }
  ];

  // Automation processes configuration
  const automationProcesses = [
    {
      id: 'commissionExpenses',
      name: t('automation.commission_expenses', 'Commission Expenses'),
      description: t('automation.commission_expenses_desc', 'Auto-create commission expenses when payroll is processed'),
      icon: '💼',
      category: 'commission'
    },
    {
      id: 'maintenanceExpenses', 
      name: t('automation.maintenance_expenses', 'Maintenance Expenses'),
      description: t('automation.maintenance_expenses_desc', 'Auto-create maintenance expenses from shipment costing'),
      icon: '🔧',
      category: 'maintenance'
    },
    {
      id: 'fuelExpenses',
      name: t('automation.fuel_expenses', 'Fuel Expenses'), 
      description: t('automation.fuel_expenses_desc', 'Auto-create fuel expenses from shipment costing'),
      icon: '⛽',
      category: 'fuel'
    },
    {
      id: 'revenueRecognition',
      name: t('automation.revenue_recognition', 'Revenue Recognition'),
      description: t('automation.revenue_recognition_desc', 'Auto-create revenue entries when shipments are completed'),
      icon: '💰',
      category: 'revenue'
    },
    {
      id: 'payrollExpenses',
      name: t('automation.payroll_expenses', 'Payroll Expenses'),
      description: t('automation.payroll_expenses_desc', 'Auto-create salary and payroll expenses'),
      icon: '👥',
      category: 'salary'
    },
    {
      id: 'paymentJournalEntries',
      name: t('automation.payment_journal_entries', 'Payment Journal Entries'),
      description: t('automation.payment_journal_entries_desc', 'Auto-create journal entries when payments are recorded'),
      icon: '💳',
      category: 'payment',
      isPaymentAutomation: true
    },
    {
      id: 'invoiceJournalEntries',
      name: t('automation.invoice_journal_entries', 'Invoice Journal Entries'),
      description: t('automation.invoice_journal_entries_desc', 'Auto-create journal entries when invoices are created'),
      icon: '📄',
      category: 'invoice',
      isInvoiceAutomation: true
    }
  ];

  // Load data on component mount
  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      loadData();
    }
  }, [isOpen, currentCompany?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load current mappings, automation settings, and chart of accounts
      const [currentMappings, chartAccounts, currentAutomation] = await Promise.all([
        accountMappingService.getExpenseCategoryMappings(currentCompany.id),
        chartOfAccountsService.getChartOfAccounts(currentCompany.id),
        accountMappingService.getAutomationSettings(currentCompany.id)
      ]);
      
      setMappings(currentMappings);
      setAccounts(chartAccounts.filter(acc => acc.isActive)); // Load all active accounts
      setAutomationSettings(currentAutomation || getDefaultAutomationSettings());
      
    } catch (error) {
      console.error('Error loading accounting settings data:', error);
      toast.error(t('common.error_loading_data', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const getDefaultAutomationSettings = () => {
    const defaults = {};
    automationProcesses.forEach(process => {
      defaults[process.id] = {
        expenseEnabled: true,
        journalEntryEnabled: true,
        accountMapping: mappings[process.category] || ''
      };
    });
    return defaults;
  };

  const handleMappingChange = (categoryId, accountCode) => {
    setMappings(prev => ({
      ...prev,
      [categoryId]: accountCode
    }));
  };

  const handleAutomationChange = (processId, field, value) => {
    setAutomationSettings(prev => ({
      ...prev,
      [processId]: {
        ...prev[processId],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (activeTab === 'mappings') {
        await accountMappingService.updateExpenseCategoryMappings(
          currentCompany.id,
          mappings,
          currentUser?.uid || 'system'
        );
        toast.success(t('accounting.expense_mappings_updated', 'Account mappings updated successfully'));
      } else {
        await accountMappingService.updateAutomationSettings(
          currentCompany.id,
          automationSettings,
          currentUser?.uid || 'system'
        );
        toast.success(t('accounting.automation_settings_updated', 'Automation settings updated successfully'));
      }
      
      onSuccess?.();
      onClose();
      
    } catch (error) {
      console.error('Error saving accounting settings:', error);
      toast.error(t('accounting.settings_update_failed', 'Failed to update settings'));
    } finally {
      setSaving(false);
    }
  };

  const getAccountName = (accountCode) => {
    const account = accounts.find(acc => acc.accountCode === accountCode);
    return account ? `${account.accountCode} - ${account.accountName}` : accountCode;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <Cog6ToothIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('accounting.accounting_settings', 'Accounting Settings')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('accounting.settings_description', 'Configure account mappings and automation settings')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={saving}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex-shrink-0">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('mappings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'mappings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BanknotesIcon className="w-4 h-4 inline mr-2" />
              {t('accounting.account_mappings', 'Account Mappings')}
            </button>
            <button
              onClick={() => setActiveTab('automation')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'automation'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CogIcon className="w-4 h-4 inline mr-2" />
              {t('automation.automation_settings', 'Automation Settings')}
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">{t('common.loading', t('common.loading'))}</span>
            </div>
          ) : (
            <div className="space-y-6">
              {activeTab === 'mappings' ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">
                      {t('accounting.mapping_info_title', 'About Account Mappings')}
                    </h3>
                    <p className="text-sm text-blue-700">
                      {t('accounting.mapping_info_description', 'These mappings determine which chart of accounts entries are used when creating journal entries for expenses. Each expense category will be mapped to the selected account code.')}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {expenseCategories.map(category => (
                      <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-3">
                          <span className="text-2xl">{category.icon}</span>
                          <div>
                            <h4 className="font-medium text-gray-900">{category.name}</h4>
                            <p className="text-xs text-gray-500">
                              {t('accounting.category_id', 'Category')}: {category.id}
                            </p>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('accounting.mapped_account', 'Mapped Account')}
                          </label>
                          <select
                            value={mappings[category.id] || ''}
                            onChange={(e) => handleMappingChange(category.id, e.target.value)}
                            className="form-select w-full"
                          >
                            <option value="">{t('accounting.select_account', 'Select Account')}</option>
                            {accounts.map(account => (
                              <option key={account.accountCode} value={account.accountCode}>
                                {account.accountCode} - {account.accountName}
                              </option>
                            ))}
                          </select>
                          
                          {mappings[category.id] && (
                            <div className="mt-2 text-xs text-gray-600">
                              {t('accounting.current_mapping', 'Current')}: {getAccountName(mappings[category.id])}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-amber-900 mb-2">
                      {t('automation.automation_info_title', 'About Automation Settings')}
                    </h3>
                    <p className="text-sm text-amber-700">
                      {t('automation.automation_info_description', 'Control which processes are automated in your accounting system. You can disable expense creation and/or journal entry creation for each process.')}
                    </p>
                  </div>

                  <div className="space-y-4">
                    {automationProcesses.map(process => (
                      <div key={process.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-start space-x-4">
                          <span className="text-3xl">{process.icon}</span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-lg font-medium text-gray-900">{process.name}</h4>
                              <div className="flex items-center space-x-4">
                                <label className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={automationSettings[process.id]?.expenseEnabled || false}
                                    onChange={(e) => handleAutomationChange(process.id, 'expenseEnabled', e.target.checked)}
                                    className="form-checkbox h-4 w-4 text-blue-600"
                                  />
                                  <span className="text-sm text-gray-700">
                                    {t('automation.create_expenses', 'Create Expenses')}
                                  </span>
                                </label>
                                <label className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={automationSettings[process.id]?.journalEntryEnabled || false}
                                    onChange={(e) => handleAutomationChange(process.id, 'journalEntryEnabled', e.target.checked)}
                                    className="form-checkbox h-4 w-4 text-green-600"
                                  />
                                  <span className="text-sm text-gray-700">
                                    {t('automation.create_journal_entries', 'Create Journal Entries')}
                                  </span>
                                </label>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mb-4">{process.description}</p>
                            
                            {(automationSettings[process.id]?.expenseEnabled || automationSettings[process.id]?.journalEntryEnabled) && (
                              <div className="bg-gray-50 rounded-lg p-3">
                                {process.isPaymentAutomation ? (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('automation.cash_account', 'Cash Account')}
                                      </label>
                                      <select
                                        value={automationSettings[process.id]?.cashAccountMapping || ''}
                                        onChange={(e) => handleAutomationChange(process.id, 'cashAccountMapping', e.target.value)}
                                        className="form-select w-full"
                                      >
                                        <option value="">{t('accounting.select_account', 'Select Account')}</option>
                                        {accounts.filter(acc => acc.accountType === 'asset' && acc.isActive).map(account => (
                                          <option key={account.accountCode} value={account.accountCode}>
                                            {account.accountCode} - {account.accountName}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('automation.bank_account', 'Bank Account')}
                                      </label>
                                      <select
                                        value={automationSettings[process.id]?.bankAccountMapping || ''}
                                        onChange={(e) => handleAutomationChange(process.id, 'bankAccountMapping', e.target.value)}
                                        className="form-select w-full"
                                      >
                                        <option value="">{t('accounting.select_account', 'Select Account')}</option>
                                        {accounts.filter(acc => acc.accountType === 'asset' && acc.isActive).map(account => (
                                          <option key={account.accountCode} value={account.accountCode}>
                                            {account.accountCode} - {account.accountName}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('automation.receivable_account', 'Accounts Receivable')}
                                      </label>
                                      <select
                                        value={automationSettings[process.id]?.receivableAccountMapping || ''}
                                        onChange={(e) => handleAutomationChange(process.id, 'receivableAccountMapping', e.target.value)}
                                        className="form-select w-full"
                                      >
                                        <option value="">{t('accounting.select_account', 'Select Account')}</option>
                                        {accounts.filter(acc => acc.accountType === 'asset' && acc.isActive).map(account => (
                                          <option key={account.accountCode} value={account.accountCode}>
                                            {account.accountCode} - {account.accountName}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                ) : process.isInvoiceAutomation ? (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('automation.revenue_account', 'Revenue Account')}
                                      </label>
                                      <select
                                        value={automationSettings[process.id]?.revenueAccountMapping || ''}
                                        onChange={(e) => handleAutomationChange(process.id, 'revenueAccountMapping', e.target.value)}
                                        className="form-select w-full"
                                      >
                                        <option value="">{t('accounting.select_account', 'Select Account')}</option>
                                        {accounts.filter(acc => acc.accountType === 'revenue' && acc.isActive).map(account => (
                                          <option key={account.accountCode} value={account.accountCode}>
                                            {account.accountCode} - {account.accountName}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('automation.receivable_account', 'Accounts Receivable')}
                                      </label>
                                      <select
                                        value={automationSettings[process.id]?.receivableAccountMapping || ''}
                                        onChange={(e) => handleAutomationChange(process.id, 'receivableAccountMapping', e.target.value)}
                                        className="form-select w-full"
                                      >
                                        <option value="">{t('accounting.select_account', 'Select Account')}</option>
                                        {accounts.filter(acc => acc.accountType === 'asset' && acc.isActive).map(account => (
                                          <option key={account.accountCode} value={account.accountCode}>
                                            {account.accountCode} - {account.accountName}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('automation.vat_account', 'VAT Payable Account')}
                                      </label>
                                      <select
                                        value={automationSettings[process.id]?.vatAccountMapping || ''}
                                        onChange={(e) => handleAutomationChange(process.id, 'vatAccountMapping', e.target.value)}
                                        className="form-select w-full"
                                      >
                                        <option value="">{t('accounting.select_account', 'Select Account')}</option>
                                        {accounts.filter(acc => acc.accountType === 'liability' && acc.isActive).map(account => (
                                          <option key={account.accountCode} value={account.accountCode}>
                                            {account.accountCode} - {account.accountName}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      {t('automation.account_mapping', 'Account Mapping')}
                                    </label>
                                    <select
                                      value={automationSettings[process.id]?.accountMapping || ''}
                                      onChange={(e) => handleAutomationChange(process.id, 'accountMapping', e.target.value)}
                                      className="form-select w-full"
                                    >
                                      <option value="">{t('accounting.select_account', 'Select Account')}</option>
                                      {accounts.map(account => (
                                        <option key={account.accountCode} value={account.accountCode}>
                                          {account.accountCode} - {account.accountName}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {(!automationSettings[process.id]?.expenseEnabled && !automationSettings[process.id]?.journalEntryEnabled) && (
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-700">
                                  ⚠️ {t('automation.manual_warning', 'You will need to create these entries manually')}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0 bg-white">
          <button
            onClick={onClose}
            className="btn btn-secondary"
            disabled={saving}
          >
            {t('common.cancel', t('common.cancel'))}
          </button>
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={loading || saving}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.saving', 'Saving...')}
              </>
            ) : (
              <>
                <CogIcon className="w-4 h-4 mr-2" />
                {t('accounting.save_mappings', 'Save Settings')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountingSettingsModal;